import { NthUnitOffer } from '../../promotion-display-utils';
import { ProductAggregate } from '../aggregate-cart';
import { FamilyOutcome, PerPromoBreakdown, UnitDiscount } from '../family-outcome.types';

/**
 * Computes the n-th unit family outcome over a product aggregate.
 *
 * N-th unit is CYCLICAL: "cada N-ésima unidad X% off" applies to the 2da, 4ta,
 * 6ta… (for N=2). Each cyclical offer produces:
 *   - K = floor(totalUnits / N) qualifying positions
 *   - discount = pct × Σ(K cheapest units globally)
 *
 * When several cyclical offers coexist on the same product they DO NOT sum:
 * they are ALTERNATIVES and the engine picks the one that produces the largest
 * total discount (best-of within the family). Only ONE offer wins → single
 * `perPromoBreakdown` entry.
 *
 * Determinism: tied totals broken by lexicographic order of `sourcePromotionId`.
 * Pure, no rounding (orchestrator rounds once at the end).
 */
export function calculateNthUnitForAggregate(
  nthUnitOffers: NthUnitOffer[],
  aggregate: ProductAggregate,
): FamilyOutcome {
  if (nthUnitOffers.length === 0 || aggregate.totalUnits === 0) {
    return { family: 'nthUnit', totalDiscount: 0, perPromoBreakdown: [] };
  }

  let winner: { offer: NthUnitOffer; unitDiscounts: UnitDiscount[]; total: number } | null = null;

  for (const offer of nthUnitOffers) {
    if (offer.nthUnit < 2 || offer.discountPercentage <= 0) {
      continue;
    }
    const k = Math.floor(aggregate.totalUnits / offer.nthUnit);
    if (k === 0) {
      continue;
    }
    const targetUnits = aggregate.units.slice(0, k);
    const unitDiscounts: UnitDiscount[] = [];
    let total = 0;
    for (const unit of targetUnits) {
      const amount = unit.basePrice * (offer.discountPercentage / 100);
      unitDiscounts.push({ unit, amount, sourcePromotionId: offer.sourcePromotionId });
      total += amount;
    }
    if (
      !winner ||
      total > winner.total ||
      (total === winner.total &&
        offer.sourcePromotionId.localeCompare(winner.offer.sourcePromotionId) < 0)
    ) {
      winner = { offer, unitDiscounts, total };
    }
  }

  if (!winner) {
    return { family: 'nthUnit', totalDiscount: 0, perPromoBreakdown: [] };
  }

  const breakdown: PerPromoBreakdown = {
    promotionId: winner.offer.sourcePromotionId,
    discountAmount: winner.total,
    unitDiscounts: winner.unitDiscounts,
  };
  return { family: 'nthUnit', totalDiscount: winner.total, perPromoBreakdown: [breakdown] };
}
